/**
 * Mappa dei blocchi di attivazione (STU-520, fase 1: misura).
 *
 * Un BLOCCO e' una superficie che accompagna la persona nei primi passi
 * (un modale, un tour, un banner). Ogni blocco ha un id, sta in un file, agisce
 * su un passo del percorso e ha una variante attiva. Le varianti nuove si
 * aggiungono qui; quelle che non servono si ritirano in base ai numeri.
 *
 * Come si misura: `tracciaBlocco(azione, id)` in `utils/analytics.js` manda a
 * GA4 `blocco_visto`, `blocco_azione` o `blocco_chiuso` con i parametri
 * `blocco`, `variante`, `passo` (presi da qui) e `dettaglio`.
 * La guida completa e il formato da copiare per un altro prodotto:
 * `Product/Conversion/Attivazione/MAPPA.md`.
 *
 * Campi di ogni voce:
 * - nome: come lo chiamiamo a voce;
 * - dove: il file del componente, relativo a `frontend/src`;
 * - passo: il passo del percorso in cui la persona si trova quando il blocco
 *   compare. Gli id sono gli stessi del contratto del server;
 * - variante_attuale: la variante che vede chi non e' in una prova;
 * - eventi_esistenti (facoltativo): il blocco era gia' misurato prima della
 *   STU-520 con questi eventi. Non si rinominano e non si duplicano: per questi
 *   blocchi `tracciaBlocco` non si chiama;
 * - montato (facoltativo, solo se false): il componente esiste ma oggi nessuna
 *   pagina lo mostra. Sta nella mappa perche' sia completa, senza misura.
 */

/** I passi del percorso, nell'ordine. Stessi id del contratto del server. */
export const PASSI = ['iscritto', 'materiale', 'pronto', 'prima_risposta', 'ritorno'];

/** La variante di partenza di ogni blocco. */
export const VARIANTE_BASE = 'base';

export const BLOCCHI = {
  // Blocchi misurati dalla STU-520 con blocco_visto / blocco_azione / blocco_chiuso

  missioni_benvenuto: {
    nome: 'Missioni di benvenuto',
    dove: 'components/gamification/GamificationOnboardingModal/GamificationOnboardingModal.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
  },
  benvenuto_genitore: {
    nome: 'Benvenuto per genitori',
    dove: 'components/gamification/ParentGamificationOnboardingModal/ParentGamificationOnboardingModal.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
  },
  suggerimenti_serie_punti: {
    nome: 'Suggerimenti su serie e punti',
    dove: 'components/gamification/GamificationStepOnboarding/GamificationStepOnboarding.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
  },
  banner_verifica_email: {
    nome: 'Banner verifica email',
    dove: 'components/emailVerificationBanner/EmailVerificationBanner.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
  },
  tour_modalita_esame: {
    nome: 'Tour delle modalita di esame',
    dove: 'pages/review/components/ExamModePanel/ExamModePanel.js',
    passo: 'pronto',
    variante_attuale: VARIANTE_BASE,
  },
  tour_interrogazione: {
    nome: 'Tour della interrogazione',
    dove: 'pages/chat/chatCard/InterrogationOnboarding/InterrogationOnboarding.js',
    passo: 'pronto',
    variante_attuale: VARIANTE_BASE,
  },
  finestra_ritorno: {
    nome: 'Finestra del ritorno',
    dove: 'components/gamification/DailyReturnModal/DailyReturnModal.js',
    passo: 'ritorno',
    variante_attuale: VARIANTE_BASE,
  },

  // Blocchi guidati dal server (STU-520, fase 2): gruppo, variante, stato e
  // parametri arrivano da GET /api/attivazione/config e la variante viaggia
  // su ogni evento. `variante_attuale` vale solo se il server non la manda.

  esempio_primo_passo: {
    nome: 'Prova subito col documento d\'esempio',
    dove: 'components/attivazione/EsempioPrimoPasso/EsempioPrimoPasso.js',
    passo: 'prima_risposta',
    variante_attuale: VARIANTE_BASE,
  },
  fine_esempio: {
    nome: 'Dopo la prima risposta, i tuoi appunti',
    dove: 'components/attivazione/FineEsempio/FineEsempio.js',
    passo: 'materiale',
    variante_attuale: VARIANTE_BASE,
  },

  // Blocchi gia' misurati prima della STU-520: restano coi loro eventi

  percorso_benvenuto: {
    nome: 'Percorso di benvenuto',
    dove: 'pages/welcome/index.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
    // 79 eventi onb_* per ruolo, catalogo in
    // server/Engineering/Firebase-Analytics/EVENTI-ONBOARDING-COMPLETI.md
    eventi_esistenti: ['onb_flow_started', 'onb_welcome_viewed', 'onb_*_viewed', 'onb_flow_abandoned_*'],
  },
  home_v2: {
    nome: 'Home nuova',
    dove: 'pages/homeV2/index.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
    eventi_esistenti: ['home_v2_viewed'],
  },
  banner_maturita: {
    nome: 'Banner Maturita',
    dove: 'containers/layout/layout.js',
    passo: 'iscritto',
    variante_attuale: VARIANTE_BASE,
    // Ingresso spento dal 13/08/2026 (config/featureFlags.js): zero per costruzione.
    eventi_esistenti: ['maturita_entry_banner_shown', 'maturita_banner_clicked', 'maturita_banner_closed'],
  },
  limite_caricamento: {
    nome: 'Limite di caricamento',
    dove: 'pages/uploadNotes/uploadNotesCard/uploadNotesForm/uploadNotesForm.js',
    passo: 'materiale',
    variante_attuale: VARIANTE_BASE,
    eventi_esistenti: ['upload_limit_reached', 'upload_limit_reached_detailed'],
  },
  offerta_prova: {
    nome: 'Offerta della prova',
    dove: 'components/trial/TrialOfferModal.js',
    passo: 'ritorno',
    variante_attuale: VARIANTE_BASE,
    eventi_esistenti: ['trial_offer_shown', 'trial_offer_cta', 'trial_offer_dismiss'],
  },
  novita_prodotto: {
    nome: 'Novita del prodotto',
    dove: 'components/productUpdates/ProductUpdatePanel/ProductUpdatePanel.js',
    passo: 'ritorno',
    variante_attuale: VARIANTE_BASE,
    eventi_esistenti: [
      'product_update_toast_shown',
      'product_update_opened',
      'product_update_snoozed',
      'product_update_acknowledged',
      'product_update_article_click',
      'product_update_suggest_click',
    ],
  },

  // Componenti che oggi nessuna pagina monta: nella mappa, senza misura

  tour_materiali: {
    nome: 'Tour dei materiali',
    dove: 'pages/materials/MaterialsOnboarding/MaterialsOnboarding.js',
    passo: 'materiale',
    variante_attuale: VARIANTE_BASE,
    montato: false,
  },
  tour_ripetizione: {
    nome: 'Tour della ripetizione',
    dove: 'pages/chat/chatCard/ReviewOnboarding/ReviewOnboarding.js',
    passo: 'pronto',
    variante_attuale: VARIANTE_BASE,
    montato: false,
  },
};

export default BLOCCHI;
